import React from 'react';
import { TrendingUp, Award, Target, Activity } from 'lucide-react';
import { useGet } from '../../hooks/useApi.js';
import { Smile, Frown, Meh, Phone } from 'lucide-react';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { cn } from '@hts/ui';
import type { Technician } from '../../api/types.js';
import { formatHours } from '../../lib/format.js';

export function CoachingPage() {
  // GET /api/v1/technicians (people.service): active flag, open tickets from
  // tickets_cache, utilization = open / maxOpenTickets, skills from tech_skills.
  const { data, loading, error, refetch } = useGet<{ items: Technician[] }>('/api/v1/technicians');
  const { data: csatData } = useGet<{ items: Array<{ technicianId: string; technicianName: string; avgSentiment: number | null; totalCalls: number; alerts: number }> }>('/api/v1/calls/csat/by-technician?days=30');
  const csatMap = new Map((csatData?.items ?? []).map((c) => [c.technicianId, c]));
  const techs = data?.items ?? [];

  const sorted = [...techs].filter(t => t.userId).sort((a, b) => a.utilization - b.utilization);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold text-[hsl(var(--content))]">Technician Coaching</h1>
        <p className="text-sm text-[hsl(var(--content-subtle))] mt-0.5">Performance insights, skill gaps, and coaching recommendations for your team.</p>
      </div>

      {/* Team summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Techs', value: techs.length, icon: '👥', color: 'text-blue-600' },
          { label: 'Active', value: techs.filter(t => t.available).length, icon: '🟢', color: 'text-green-600' },
          { label: 'At Capacity', value: techs.filter(t => t.utilization >= 0.9).length, icon: '🔴', color: 'text-red-600' },
          { label: 'Avg Utilization', value: techs.length ? `${Math.round(techs.reduce((s, t) => s + t.utilization, 0) / techs.length * 100)}%` : '—', icon: '📊', color: 'text-amber-600' },
        ].map(s => (
          <div key={s.label} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-4 py-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">{s.icon}</span>
              <p className={`text-2xl font-bold tabular-nums ${s.color}`}>{loading ? '…' : error ? '—' : s.value}</p>
            </div>
            <p className="text-xs text-[hsl(var(--content-subtle))]">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Coaching cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} lines={4} />)}
        </div>
      ) : error ? (
        <EmptyState
          icon="⚠️"
          title="Couldn't load technicians"
          description={error.includes('401') ? 'Your session expired — log out and back in.' : `${error} — try again.`}
          action={<button onClick={refetch} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700">Try again</button>}
        />
      ) : sorted.length === 0 ? (
        <EmptyState
          icon="👥"
          title="No technicians yet"
          description="Technicians are created from ConnectWise members by the sync. Check the ConnectWise integration and People → Technicians."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sorted.map(tech => <TechCoachCard key={tech.id} tech={tech} csat={csatMap.get(tech.id)} />)}
        </div>
      )}
    </div>
  );
}

function TechCoachCard({ tech, csat }: { tech: Technician; csat?: { technicianId: string; technicianName: string; avgSentiment: number | null; totalCalls: number; alerts: number } }) {
  const utilizationPct = Math.round(tech.utilization * 100);
  const topSkills = [...tech.skills].sort((a, b) => b.level - a.level).slice(0, 3);
  const skillGaps = tech.skills.filter(s => s.level === 0);

  const utilizationColor = tech.utilization >= 0.9 ? 'text-red-600' : tech.utilization >= 0.7 ? 'text-amber-600' : 'text-green-600';
  const barColor = tech.utilization >= 0.9 ? 'bg-red-500' : tech.utilization >= 0.7 ? 'bg-amber-500' : 'bg-green-500';

  // utilization is 0 when the tech has no max-open-tickets capacity configured,
  // so don't call that "underutilized" when they actually carry tickets.
  const capacityUnset = tech.utilization === 0 && tech.openTickets > 0;
  const recommendations: string[] = [];
  if (tech.utilization >= 0.9) recommendations.push('Reduce load — at or near capacity');
  if (capacityUnset) recommendations.push('No max open tickets set — set capacity in People → Technicians to track utilization');
  else if (tech.openTickets === 0 && tech.available) recommendations.push('No open tickets — available for new work');
  else if (tech.utilization < 0.4) recommendations.push('Assign more tickets — underutilized');
  if (skillGaps.length > 0) recommendations.push(`Skills not yet rated: ${skillGaps.map(s => s.skillName).slice(0, 3).join(', ')}`);
  if (!tech.available) recommendations.push('Inactive in ConnectWise — not receiving dispatches');
  if (recommendations.length === 0) recommendations.push('On track — good balance of load and skills');

  // CSAT sentiment display
  const SentimentIcon = csat?.avgSentiment ? (csat.avgSentiment >= 4 ? Smile : csat.avgSentiment >= 3 ? Meh : Frown) : null;
  const sentimentColor = csat?.avgSentiment ? (csat.avgSentiment >= 4 ? 'text-green-600' : csat.avgSentiment >= 3 ? 'text-yellow-600' : 'text-red-600') : 'text-[hsl(var(--content-subtle))]';

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-5 shadow-sm space-y-4">
      {/* Tech header */}
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-brand-400 to-brand-700 flex items-center justify-center text-white font-bold text-sm shrink-0">
          {tech.avatarInitials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[hsl(var(--content))] truncate">{tech.name}</p>
          <p className="text-xs text-[hsl(var(--content-subtle))]">{[tech.role, tech.board].filter(Boolean).join(' · ') || 'Technician'}</p>
        </div>
        <div className={cn(
          'flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold',
          tech.available
            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
        )}>
          <span className={cn('h-1.5 w-1.5 rounded-full', tech.available ? 'bg-green-500' : 'bg-red-500')} />
          {tech.available ? 'Active' : 'Inactive'}
        </div>
      </div>

      {/* Utilization */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-[hsl(var(--content-subtle))]">Utilization</span>
          <span className={`text-sm font-bold tabular-nums ${utilizationColor}`}>{capacityUnset ? 'n/a' : `${utilizationPct}%`}</span>
        </div>
        <div className="h-2 w-full rounded-full bg-[hsl(var(--surface-subtle))]">
          <div className={`h-full rounded-full transition-all duration-700 ${barColor}`} style={{ width: `${utilizationPct}%` }} />
        </div>
        <div className="flex justify-between text-[10px] text-[hsl(var(--content-placeholder))]">
          <span>{tech.openTickets} open tickets</span>
          <span>{formatHours(tech.capacityHours)} capacity/day</span>
        </div>
      </div>

      {/* Top skills */}
      <div>
        <p className="text-xs font-semibold text-[hsl(var(--content-subtle))] mb-2 flex items-center gap-1.5">
          <Award className="h-3 w-3" /> Top Skills
        </p>
        <div className="flex flex-wrap gap-1.5">
          {topSkills.length === 0 && (
            <span className="text-xs text-[hsl(var(--content-placeholder))]">No skills recorded yet</span>
          )}
          {topSkills.map(s => (
            <span key={s.skillId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 text-xs font-medium text-brand-700 dark:text-brand-300">
              {'★'.repeat(Math.max(0, Math.min(4, s.level)))}{'☆'.repeat(4 - Math.max(0, Math.min(4, s.level)))} {s.skillName}
            </span>
          ))}
        </div>
      </div>

      {/* Coaching recommendations */}
      <div className="rounded-xl bg-[hsl(var(--surface-subtle))] px-3 py-2.5 space-y-1.5">
        <p className="text-[10px] font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide flex items-center gap-1">
          <Target className="h-3 w-3" /> Coaching Notes
        </p>
        {recommendations.map((r, i) => (
          <p key={i} className="text-xs text-[hsl(var(--content))] flex items-start gap-1.5">
            <span className="mt-0.5 text-brand-500">›</span> {r}
          </p>
        ))}
      </div>
    </div>
  );
}
